/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import {
    object, string, shape, func, arrayOf, number, bool, array,
} from 'prop-types';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useUIDSeed } from 'react-uid';
import { withRouter } from 'react-router-dom';
import { actions as tagManagerActions } from '../../../../../../state/ducks/TagManager';
import GraphqlClubSelection from '../GraphqlClubSelection/GraphqlClubSelection';
import RedirectToSku from '../common/RedirectToSku';

const CommonRadioGroup = ({
    classes,
    trackEvent,
    skuData,
    handleSkuChange,
    selectedSku,
    getRadioLabel,
    presentation_family,
    customizedRadio, // (pass customizedRadio as paramter if you want to use customized radio button,
    // dont pass customizedRadio as parameter if you want to use normal radio button.)
    productType,
    renderSkuPrice,
    skuMonths,
    changeClubMonth,
    changeClubLength,
    clubUpsells,
    selectedClubUpsells,
    changeClubUpsell,
    parentPartNumber,
    baseProductSku,
}) => {
    const seed = useUIDSeed();
    const isFoodBrand = presentation_family === 'food';

    const tracker = (sku) => {
        trackEvent({
            eventName: 'feature_interaction',
            eventCategory: 'Product Page',
            eventAction: 'Size Selection',
            eventLabel: sku.name,
        });
    };

    if (isFoodBrand && productType?.toLowerCase() === 'club') {
        return (
            <GraphqlClubSelection
                skuData={skuData}
                changeClubLength={changeClubLength}
                clubUpsells={clubUpsells}
                selectedSku={selectedSku}
                tracker={tracker}
                changeClubMonth={changeClubMonth}
                skuMonths={skuMonths}
                renderSkuPrice={renderSkuPrice}
                selectedClubUpsells={selectedClubUpsells}
                changeClubUpsell={changeClubUpsell}
            />
        );
    }
    let skuDataList = skuData;
    if (baseProductSku) {
        const parentSkuIndex = skuDataList.findIndex((sku) => sku?.id === parentPartNumber);
        const parentSku = skuDataList.filter((sku) => sku?.id === parentPartNumber);
        if (parentSkuIndex) {
            const skuList = skuDataList.filter((sku) => sku.id !== parentPartNumber);
            skuDataList = [...parentSku, ...skuList];
        }
    }
    return (
        <RadioGroup
            aria-label="Product Skus"
            name="skus"
            onChange={customizedRadio ? handleSkuChange : null}
            value={customizedRadio ? selectedSku.id : null}
            role="radiogroup"  // Ensure the role is explicitly set here
        >
            {(skuDataList || []).map((sku) => (
                <RedirectToSku
                    selectedSkuId={selectedSku?.id}
                    onChange={handleSkuChange}
                >
                    {({ redirectToSku }) => (
                        <FormControlLabel
                            key={seed(sku)}
                            value={sku.id}
                            id={`radio-button-${sku.id}`}
                            data-test="pdp-radio-button"
                            name="pdpRadioButton"
                            label={(getRadioLabel(sku))}
                            onChange={!customizedRadio ? handleSkuChange : null}
                            classes={isFoodBrand ? { root: classes.formControlRoot, label: classes.formControlLabel } : { label: classes.formLabel }}
                            control={customizedRadio
                                ? (
                                    <Radio
                                        role="radio"
                                        aria-labelledby={`radio-button-${sku?.id}`} // Associate with the label using `aria-labelledby`
                                        // eslint-disable-next-line no-underscore-dangle
                                        className={sku.__typename === 'Upsell' ? classes.rootUpsell : classes.root}
                                        checkedIcon={<span className={`${classes.icon} ${classes.checkedIcon}`} />}
                                        icon={<span className={classes.icon} />}
                                        classes={{
                                            root: classes.radio,
                                            checked: classes.checked,
                                        }}
                                        onClick={(e) => {
                                            tracker(sku);

                                            // eslint-disable-next-line no-underscore-dangle
                                            if (sku?.__typename === 'Upsell' && isFoodBrand) {
                                                redirectToSku(e, sku);
                                            }
                                        }}
                                        checked={selectedSku.id === sku.id}
                                    />
                                )
                                : (
                                    <Radio
                                        role="radio"
                                        aria-labelledby={`radio-button-${sku?.id}`} // Associate with the label using `aria-labelledby`
                                        classes={{
                                            root: classes.radio,
                                            checked: classes.checked,
                                        }}
                                        onClick={() => {
                                            tracker(sku);
                                        }}
                                        checked={selectedSku.id === sku.id}
                                    />
                                )}
                        />
                    )}
                </RedirectToSku>
            ))}
        </RadioGroup>
    );
};

const mapDispatchToProps = {
    trackEvent: tagManagerActions.trackEvent,
};

CommonRadioGroup.defaultProps = {
    renderSkuPrice: () => { },
    skuMonths: {},
    clubUpsells: [],
    changeClubMonth: () => { },
    changeClubLength: () => { },
    customizedRadio: false,
    productType: null,
    selectedClubUpsells: null,
    changeClubUpsell: () => { },
    baseProductSku: null,
};

CommonRadioGroup.propTypes = {
    classes: object.isRequired,
    trackEvent: func.isRequired,
    parentPartNumber: string.isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ).isRequired,
    handleSkuChange: func.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    getRadioLabel: func.isRequired,
    presentation_family: string.isRequired,
    customizedRadio: bool,
    renderSkuPrice: func,
    changeClubMonth: func,
    productType: string,
    skuMonths: object,  // will replace with shape method once club GQL ready
    changeClubLength: func,
    clubUpsells: array,  // will replace with shape method once club GQL ready
    selectedClubUpsells: string,
    changeClubUpsell: func,
    baseProductSku: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                    __typename: string.isRequired,
                }),
            ).isRequired,
            __typename: string.isRequired,
        }),
    ),
};

export default withRouter(connect('', mapDispatchToProps)(CommonRadioGroup));
